import {
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Paper,
  Box,
  Grid,
  Collapse,
  TableHead,
  Avatar,
  Tooltip,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import {
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
} from "@material-ui/icons";
import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";
import Scrollbar from "src/components/Scrollbar";
import { TablaHead, TablaToolbar } from "src/components/_dashboard/tablas";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import {
  capitalizarPalabras,
  capitalizarPrimeraLetra,
} from "src/utils/sharedFunctions";
import SearchNotFound from "src/components/SearchNotFound";
import { ThemeProvider } from "@material-ui/styles";
import CustomTablePagination from "src/components/CustomTablePagination";
import { applySortFilter, getComparator } from "src/utils/sharedTables";
import * as locales from "@material-ui/core/locale";
import ExportToExcel from "src/components/ExportToExcel";
import {
  mapPropuestasParaExportacion,
  generarNombreArchivoExportacion,
} from "src/utils/propuestasExport";
import { useSelector } from "react-redux";
import { formatearFecha } from "src/utils/formatTime";

const TABLE_HEAD = [
  { id: "position", label: "N°", alignRight: false },
  { id: "tipo", label: "Tipo", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "cliente", label: "Cliente/Solicitante", alignRight: false },
  { id: "vendedor", label: "Vendedor", alignRight: false },
  { id: "servicios", label: "Servicios/Precio", alignRight: false },
  { id: "fechaCreacion", label: "F. Creación", alignRight: false },
  { id: "fechaEnvio", label: "F. Envío", alignRight: false },
  { id: "acciones", label: "Acciones", alignRight: false },
];

ModalDetalleResumenPropuestas.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  setOpen: PropTypes.func,
  propuestas: PropTypes.array,
  cargandoPropuestas: PropTypes.bool,
  estado: PropTypes.string,
  fechaInicio: PropTypes.string,
  fechaFin: PropTypes.string,
};

export default function ModalDetalleResumenPropuestas({
  isOpen,
  setOpen,
  propuestas = [],
  cargandoPropuestas,
  title,
  estado,
  fechaInicio,
  fechaFin,
}) {
  const { sucursal } = useSelector((state) => state.auth.user);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("fechaCreacion");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleClose = () => {
    setOpen(false);
    // Reset states
    setPage(0);
    setSelected([]);
    setFilterName("");
    setExpandedRows(new Set());
  };

  const handleToggleExpanded = (propuestaId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propuestaId)) {
        newSet.delete(propuestaId);
      } else {
        newSet.add(propuestaId);
      }
      return newSet;
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = propuestas.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const filteredPropuestas = useMemo(() => {
    if (!filterName.trim()) {
      return applySortFilter(propuestas, getComparator(order, orderBy), "", []);
    }

    // Filtrar propuestas que coincidan en campos básicos O en servicios
    const propuestasFiltradas = propuestas.filter((propuesta) => {
      const filterTerm = filterName.toLowerCase();

      // Buscar en campos básicos
      const basicFields = [
        propuesta.tipo,
        propuesta.estado,
        propuesta.nombreSolicitante,
        propuesta.nombreCliente,
        propuesta.nombreVendedor,
        propuesta.rutSolicitante,
        propuesta.rutCliente,
        propuesta.sucursal,
      ];

      const matchBasicFields = basicFields.some((field) =>
        field?.toLowerCase().includes(filterTerm),
      );

      // Buscar en servicios (planes o tarea)
      let matchServices = false;
      if (propuesta.tipo === "PLAN" && propuesta.planes) {
        matchServices = propuesta.planes.some((plan) =>
          plan.nombrePlan?.toLowerCase().includes(filterTerm),
        );
      } else if (propuesta.tipo === "TAREA" && propuesta.nombreTarea) {
        matchServices = propuesta.nombreTarea
          .toLowerCase()
          .includes(filterTerm);
      }

      return matchBasicFields || matchServices;
    });

    return applySortFilter(
      propuestasFiltradas,
      getComparator(order, orderBy),
      "",
      [],
    );
  }, [filterName, order, orderBy, propuestas]);

  // Datos mapeados para exportación
  const datosExportacion = useMemo(() => {
    return mapPropuestasParaExportacion(
      filteredPropuestas,
      sucursal?.refSucursal,
    );
  }, [filteredPropuestas, sucursal?.refSucursal]);

  // Nombre del archivo para exportación
  const nombreArchivo = useMemo(() => {
    return generarNombreArchivoExportacion(
      estado || "propuestas",
      fechaInicio,
      fechaFin,
      sucursal?.refSucursal,
    );
  }, [estado, fechaInicio, fechaFin, sucursal?.refSucursal]);

  const isUserNotFound = filteredPropuestas.length === 0 && !cargandoPropuestas;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="dialog-modal-resumen-propuestas"
      id="dialog-modal-resumen-propuestas"
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle
        htmlFor="dialog-modal-resumen-propuestas"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          pb: 2,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        {/* Header con filtros y exportación */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid item xs={12} md={8}>
            <TablaToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              placeholder="Buscar por cliente, vendedor, estado o servicio..."
              disabled={cargandoPropuestas}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <ExportToExcel
              data={datosExportacion}
              filename={nombreArchivo}
              sheetName="propuestas_filtradas"
              type="objects"
              disabled={cargandoPropuestas || filteredPropuestas.length === 0}
            />
          </Grid>
        </Grid>

        <TablaPropuestasEnhanced
          propuestas={filteredPropuestas}
          cargandoPropuestas={cargandoPropuestas}
          isUserNotFound={isUserNotFound}
          selected={selected}
          order={order}
          orderBy={orderBy}
          page={page}
          rowsPerPage={rowsPerPage}
          expandedRows={expandedRows}
          onToggleExpanded={handleToggleExpanded}
          onRequestSort={handleRequestSort}
          onSelectAllClick={handleSelectAllClick}
          onClick={handleClick}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingButton onClick={handleClose} color="error" variant="outlined">
          Cerrar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// Componente tabla mejorado con expansión
const TablaPropuestasEnhanced = memo(
  ({
    propuestas,
    cargandoPropuestas,
    isUserNotFound,
    selected,
    order,
    orderBy,
    page,
    rowsPerPage,
    expandedRows,
    onToggleExpanded,
    onRequestSort,
    onSelectAllClick,
    onClick,
    onChangePage,
    onChangeRowsPerPage,
  }) => {
    const theme = createTheme({}, locales.esES);

    // Función para obtener el color del chip según el estado
    const getEstadoColor = (estado) => {
      switch (estado?.toUpperCase()) {
        case "ACEPTADA":
          return "success";
        case "RECHAZADA":
          return "error";
        case "ENVIADA":
          return "info";
        case "CREADA":
          return "warning";
        default:
          return "default";
      }
    };

    // Función para calcular el valor total de una propuesta
    const calcularValorTotal = (propuesta) => {
      if (propuesta.tipo === "PLAN" && propuesta.planes) {
        const total = propuesta.planes.reduce(
          (sum, plan) => sum + (plan.valorPlan || 0),
          0,
        );
        const tipoMoneda = propuesta.planes[0]?.tipoMonedaPlan || "";
        return { total, tipoMoneda };
      } else if (propuesta.tipo === "TAREA") {
        return {
          total: propuesta.precioFinal || 0,
          tipoMoneda: propuesta.tipoMoneda || "",
        };
      }
      return { total: 0, tipoMoneda: "" };
    };

    // Función para obtener información de servicios
    const getServiciosInfo = (propuesta) => {
      if (propuesta.tipo === "PLAN" && propuesta.planes) {
        return {
          cantidad: propuesta.planes.length,
          descripcion:
            propuesta.planes.length > 1
              ? `${propuesta.planes.length} planes`
              : propuesta.planes[0]?.nombrePlan || "Plan",
        };
      } else if (propuesta.tipo === "TAREA") {
        return {
          cantidad: 1,
          descripcion: propuesta.nombreTarea || "Tarea especial",
        };
      }
      return { cantidad: 0, descripcion: "Sin servicios" };
    };

    if (cargandoPropuestas) {
      return (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableBody>
                  {[...Array(10)].map((_, index) => (
                    <TableRow key={index}>
                      {TABLE_HEAD.map((headCell) => (
                        <TableCell key={headCell.id}>
                          <Skeleton variant="text" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Paper>
      );
    }

    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TablaHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={propuestas.length}
                numSelected={selected.length}
                onRequestSort={onRequestSort}
                onSelectAllClick={onSelectAllClick}
              />
              <TableBody>
                {propuestas
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = selected.indexOf(row.id) !== -1;
                    const isExpanded = expandedRows.has(row.id);
                    const valorTotal = calcularValorTotal(row);
                    const serviciosInfo = getServiciosInfo(row);

                    return (
                      <>
                        {/* Fila principal */}
                        <TableRow
                          hover
                          key={row.id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {page * rowsPerPage + index + 1}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={row.tipo}
                              size="small"
                              variant="outlined"
                              color={
                                row.tipo === "PLAN" ? "primary" : "secondary"
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={capitalizarPrimeraLetra(row.estado)}
                              size="small"
                              color={getEstadoColor(row.estado)}
                            />
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: "primary.light",
                                }}
                              >
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  noWrap
                                >
                                  {capitalizarPalabras(
                                    row.nombreSolicitante ||
                                      row.nombreCliente ||
                                      "N/A",
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {formatRut(
                                    row.rutSolicitante || row.rutCliente || "",
                                    RutFormat.DOTS_DASH,
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: "secondary.light",
                                }}
                              >
                                <BusinessIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" noWrap>
                                {capitalizarPalabras(
                                  row.nombreVendedor || "N/A",
                                )}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={`${serviciosInfo.cantidad} servicio${
                                  serviciosInfo.cantidad > 1 ? "s" : ""
                                }`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {valorTotal.total > 0
                                    ? `${valorTotal.total} ${valorTotal.tipoMoneda}`
                                    : "S/P"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  noWrap
                                >
                                  {serviciosInfo.descripcion.length > 20
                                    ? `${serviciosInfo.descripcion.substring(
                                        0,
                                        20,
                                      )}...`
                                    : serviciosInfo.descripcion}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.fechaCreacion
                                ? formatearFecha(
                                    row.fechaCreacion,
                                    "DD/MM/YYYY",
                                  )
                                : "N/A"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.fechaEnvioCorreo
                                ? formatearFecha(
                                    row.fechaEnvioCorreo,
                                    "DD/MM/YYYY",
                                  )
                                : "No enviada"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {row.urlPropuesta && (
                                <Tooltip title="Ver propuesta">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      window.open(row.urlPropuesta, "_blank")
                                    }
                                    color="primary"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip
                                title={
                                  isExpanded
                                    ? "Ocultar detalles"
                                    : "Ver detalles"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => onToggleExpanded(row.id)}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Fila expandible para detalles */}
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={9}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box margin={2}>
                                <DetallesPropuesta propuesta={row} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={TABLE_HEAD.length}
                      sx={{ py: 3 }}
                    >
                      <SearchNotFound searchQuery="" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <ThemeProvider theme={theme}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <CustomTablePagination
              count={propuestas.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </ThemeProvider>
      </Paper>
    );
  },
);

// Componente para mostrar detalles expandidos
const DetallesPropuesta = ({ propuesta }) => {
  if (propuesta.tipo === "PLAN") {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Detalles de la Propuesta de Planes
          </Typography>
        </Grid>

        {/* Información del supervisor */}
        {propuesta.nombreSupervisor && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                SUPERVISOR
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {capitalizarPalabras(propuesta.nombreSupervisor)}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Información adicional */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              INFORMACIÓN ADICIONAL
            </Typography>
            <Typography variant="body2">
              <strong>Sucursal:</strong> {propuesta.sucursal}
            </Typography>
            <Typography variant="body2">
              <strong>Teléfono:</strong>{" "}
              {propuesta.telefonoSolicitante || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {propuesta.correoSolicitante || "N/A"}
            </Typography>
          </Paper>
        </Grid>

        {/* Tabla de planes */}
        {propuesta.planes && propuesta.planes.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Planes Incluidos ({propuesta.planes.length})
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Plan</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Cliente Final</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Empleado Encargado</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Valor</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Descuento</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {propuesta.planes.map((plan, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {capitalizarPalabras(plan.nombrePlan)}
                          </Typography>
                          {plan.tipoPlan?.descripcion && (
                            <Typography variant="caption" color="textSecondary">
                              {plan.tipoPlan.descripcion.substring(0, 100)}...
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {capitalizarPalabras(plan.cliente?.nombre || "N/A")}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatRut(
                              plan.cliente?.rut || "",
                              RutFormat.DOTS_DASH,
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {capitalizarPalabras(
                              `${plan.empleado?.nombre || ""} ${
                                plan.empleado?.apellidos || ""
                              }`,
                            )}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {plan.empleado?.correo || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {plan.valorPlan} {plan.tipoMonedaPlan}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {plan.descuento}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Comentarios si existen */}
        {propuesta.comentarios && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                COMENTARIOS
              </Typography>
              <Typography variant="body2">{propuesta.comentarios}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  }

  // Para propuestas de TAREA
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Detalles de la Tarea Especial
        </Typography>
      </Grid>

      {/* Información del servicio */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            SERVICIO
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AssignmentIcon color="primary" />
            <Typography variant="body1" fontWeight="medium">
              {capitalizarPalabras(propuesta.nombreTarea || "N/A")}
            </Typography>
          </Box>
          <Typography variant="h6" color="primary">
            {propuesta.precioFinal} {propuesta.tipoMoneda}
          </Typography>
          {!!propuesta.vendidoTaxtic && (
            <Chip label="Vendido por Taxtic" size="small" color="info" />
          )}
        </Paper>
      </Grid>

      {/* Información del empleado encargado */}
      {propuesta.nombreEmpleado && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              EMPLEADO ENCARGADO
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {capitalizarPalabras(propuesta.nombreEmpleado)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {propuesta.correoEmpleado || "N/A"}
            </Typography>
          </Paper>
        </Grid>
      )}

      {/* Información del supervisor */}
      {propuesta.nombreSupervisor && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              SUPERVISOR
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {capitalizarPalabras(propuesta.nombreSupervisor)}
            </Typography>
          </Paper>
        </Grid>
      )}

      {/* Información adicional */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            INFORMACIÓN ADICIONAL
          </Typography>
          <Typography variant="body2">
            <strong>Sucursal:</strong> {propuesta.sucursal}
          </Typography>
          <Typography variant="body2">
            <strong>Teléfono:</strong> {propuesta.telefonoSolicitante || "N/A"}
          </Typography>
          <Typography variant="body2">
            <strong>Email:</strong> {propuesta.correoSolicitante || "N/A"}
          </Typography>
        </Paper>
      </Grid>

      {/* Comentarios si existen */}
      {propuesta.comentarios && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              COMENTARIOS
            </Typography>
            <Typography variant="body2">{propuesta.comentarios}</Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};
